import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import React, { FC, useCallback, useEffect, useState } from "react";
import { GridToolbarContainer } from "@material-ui/data-grid";

import BaseDataTable from "../../../../../components/modules/BaseDataTable";
import Button from "../../../../../components/elements/Button";
import CreativeAssetsManagement from "./CreativeAssetsManagement";
import LandingPageEditMode from "./LandingPageEditMode";
import LandingPageViewMode from "./LandingPageViewMode";
import UploadCreativesModal from "./UploadCreativesModal";
import UploadFromTemplateModal from "./UploadFromTemplateModal";
import api from "../../../../../api";
import classes from "../../CampaignsDashboard.module.scss";
import { AdGroup, CreativeAsset } from "../../../../../types/adGroupTypes";
import { AdLibCreativeReportType, BrandgenCampaignPool, ReportTimeFrames } from "../../../../../types/campaignTypes";
import { errorToaster } from "../../../../../helpers/toasters";
import { truncateText } from "../../../../../helpers/truncateText";

type AdGroupsCreativesMap = Record<string, CreativeAsset[]>;
type CreativeReportsMap = Record<string, AdLibCreativeReportType[]>;

type AdgroupsDashboardProps = {
  selectedCampaign: BrandgenCampaignPool;
  selectedTimeFrame: ReportTimeFrames;
  customReportDate: Date | undefined;
  isCustomDateChanged: boolean;
};

const fetchData = async (adGroupId: string, date?: Date) => {
  try {
    if (date) {
      const { payload } = await api.getAdGroupWithDailyCreativeReports(adGroupId, date);
      return payload;
    }
    const { payload } = await api.getAdGroupWithCreativeReports(adGroupId);
    return payload;
  } catch (error) {
    throw new Error((error as any).message);
  }
};

const fetchAdGroup = async (brandgenCampaignPoolId: string) => {
  try {
    const { payload } = await api.getAdGroupByCampaignId(brandgenCampaignPoolId);
    return payload;
  } catch (error) {
    throw new Error((error as any).message);
  }
};

const AdGroupsCreativesDashboard: FC<AdgroupsDashboardProps> = ({
  selectedCampaign,
  selectedTimeFrame,
  customReportDate,
  isCustomDateChanged,
}) => {
  const [adGroupsCreativesMap, setAdGroupsCreativesMap] = useState<AdGroupsCreativesMap>({});
  const [adGroup, setAdGroup] = useState<AdGroup | undefined>(undefined);
  const [creativeReportsMap, setCreativeReportsMap] = useState<CreativeReportsMap>({});
  const [isFetchingCreatives, setIsFetchingCreatives] = useState<boolean>(false);
  const [isAdGroupsPending, setIsAdGroupsPending] = useState<boolean>(false);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [isOpenModalTemplates, setIsOpenModalTemplates] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [landingPage, setLandingPage] = useState<string>("");
  const [fetchedAdGroupId, setFetchedAdGroupId] = useState<string>("");

  const columns = [
    { key: "addNewCreatives", label: "", minWidth: 100 },
    { key: "name", label: "Name", minWidth: 170 },
    { key: "createdBy", label: "Created By", minWidth: 170 },
    { key: "createdDate", label: "Created Date", minWidth: 170 },
  ] as const;

  const resetStates = () => {
    setFetchedAdGroupId("");
    setAdGroup(undefined);
    setIsAdGroupsPending(false);
  };

  const fetchAndSetAdGroup = useCallback(async () => {
    try {
      setIsAdGroupsPending(true);
      const fetchedAdGroup = await fetchAdGroup(selectedCampaign.brandgenCampaignPoolId);

      if (!fetchedAdGroup) {
        resetStates();
        return;
      }

      setFetchedAdGroupId(fetchedAdGroup.adGroupId);
      setAdGroup(fetchedAdGroup);
    } catch (error_) {
      const error = error_ as any;
      errorToaster(`There was an error: ${error.message}`);
    } finally {
      setIsAdGroupsPending(false);
    }
  }, [selectedCampaign]);

  const isCustomDate = selectedTimeFrame === ReportTimeFrames.CUSTOM_DATE;

  const fetchAndSetCreatives = useCallback(async () => {
    try {
      if (!fetchedAdGroupId) {
        resetStates();
        return;
      }

      setIsFetchingCreatives(true);

      const response = await fetchData(
        fetchedAdGroupId,
        isCustomDate && customReportDate && isCustomDateChanged ? customReportDate : undefined,
      );

      // eslint-disable-next-line consistent-return
      if (!response) return resetStates();

      const { creativesWithAdGroup, creativesReports } = response;

      setAdGroupsCreativesMap({ [fetchedAdGroupId]: creativesWithAdGroup.creatives });
      setCreativeReportsMap({ [fetchedAdGroupId]: creativesReports });

      setLandingPage(creativesWithAdGroup.creatives?.[0]?.click_url || "");
    } catch (error_) {
      const error = error_ as any;
      errorToaster(`There was an error 1: ${error.message}`);
    } finally {
      setIsFetchingCreatives(false);
    }
  }, [fetchedAdGroupId, isCustomDate, customReportDate, isCustomDateChanged]);

  useEffect(() => {
    if (selectedCampaign.brandgenCampaignPoolId) {
      fetchAndSetAdGroup();
    }
  }, [selectedCampaign.brandgenCampaignPoolId, fetchAndSetAdGroup]);

  useEffect(() => {
    if (fetchedAdGroupId) {
      fetchAndSetCreatives();
    }
  }, [fetchedAdGroupId, fetchAndSetCreatives]);

  useEffect(() => {
    if (adGroup && adGroupsCreativesMap[adGroup.adGroupId]?.[0]?.creativeType === "video/mp4") {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [adGroupsCreativesMap, adGroup]);

  const rows = [
    adGroup
      ? {
          id: adGroup.adGroupId,
          name: (
            <Tooltip title={adGroup.name}>
              <Typography>{truncateText(adGroup.name, 36)}</Typography>
            </Tooltip>
          ),
          createdBy: (
            <Tooltip title={adGroup.createdBy}>
              <Typography>{truncateText(adGroup.createdBy, 36)}</Typography>
            </Tooltip>
          ),
          createdDate: new Date(adGroup.createdAt).toLocaleDateString(),
          addNewCreatives: (
            <Button onClick={() => setIsOpenModal(true)} variant="contained" disabled={disabled}>
              Upload New AdGroup
            </Button>
          ),
          collapsableConfigs: {
            collapsable: true,
            collapsableChild: (
              <CreativeAssetsManagement
                isFetchingCreatives={isFetchingCreatives}
                creativeAssets={adGroupsCreativesMap[adGroup.adGroupId] ?? []}
                creativeDailyReports={creativeReportsMap[adGroup.adGroupId] ?? []}
                selectedTimeFrame={selectedTimeFrame}
              />
            ),
            expandControlHandler: () => fetchAndSetCreatives(),
          },
        }
      : {
          id: "",
          name: "",
          createdBy: "",
          createdDate: "",
          addNewCreatives: "",
          uploadSavedCreatives: "",
        },
  ];

  return (
    <div className={classes.dashboardContainer}>
      <GridToolbarContainer className={classes.toolbar}>
        <Typography variant="h3">Campaign Creative List</Typography>
        {isEditMode ? (
          <LandingPageEditMode
            landingPage={landingPage}
            changeMode={() => setIsEditMode(false)}
            adGroupId={adGroup?.adGroupId}
            fetchAndSetCreatives={fetchAndSetCreatives}
          />
        ) : (
          <LandingPageViewMode landingPage={landingPage} changeMode={() => setIsEditMode(true)} />
        )}
      </GridToolbarContainer>

      {isAdGroupsPending ? (
        <Typography className={classes.loaderLabel}>Please, wait while we get everything in order...</Typography>
      ) : adGroup === undefined ? (
        <Typography className={classes.noAdGroupsText}>
          Unfortunately there is no information about Ad Groups...
        </Typography>
      ) : (
        <BaseDataTable columns={columns} rows={rows} />
      )}

      <UploadCreativesModal
        isOpen={isOpenModal}
        handleClose={() => setIsOpenModal(false)}
        adLibAdvertiserId={selectedCampaign.adLibAdvertiserId}
        brandgenAdvertiserId={selectedCampaign.brandgenAdvertiserId}
        landingPage={landingPage}
        fetchedAdGroupId={fetchedAdGroupId}
        fetchAndSetAdGroup={fetchAndSetAdGroup}
        brandgenCampaignId={selectedCampaign.brandgenCampaignPoolId}
        fetchedCreativesAssets={adGroupsCreativesMap[fetchedAdGroupId] ?? []}
      />
      <UploadFromTemplateModal
        isOpen={isOpenModalTemplates}
        handleClose={() => setIsOpenModalTemplates(false)}
        adLibAdvertiserId={selectedCampaign.adLibAdvertiserId}
        brandgenAdvertiserId={selectedCampaign.brandgenAdvertiserId}
        landingPage={landingPage}
        fetchedAdGroupId={fetchedAdGroupId}
        fetchAndSetAdGroup={fetchAndSetAdGroup}
        brandgenCampaignId={selectedCampaign.brandgenCampaignPoolId}
        fetchedCreativesAssets={adGroupsCreativesMap[fetchedAdGroupId] ?? []}
      />
    </div>
  );
};

export default AdGroupsCreativesDashboard;
